import { Subtitle1, PageTitle, Subtitle2 } from "../brand/typographies";
import { useContext, useEffect, useRef, useState } from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import { Section } from "../components/layout/Section";
import { address, addressEl } from "../utils/address";
import { IFacility } from "../types/IFacility";
import { StorSelfButton } from "../components/buttons/StorSelfButton";
import { useStoreSelfNavigation } from "../hooks/useStorSelfNavigation";
import useAnalytics from "../hooks/useAnalytics";
import { useLocation } from "react-router-dom";
import { FacilitiesContext } from "../providers/facility-provider/FacilitiesContext";

export const FacilitySelectWrapper = ({ children }: { children: React.ReactNode }) => {
    const { closestFacility, selectedFacility, setSelectedFacility, facilities, currentPosition: { loading } } = useContext(FacilitiesContext)

    if (selectedFacility) {
        return children;
    }

    const handleContinue = (value: IFacility) => {
        if (value) {
            setSelectedFacility!(value)
        }
    }

    return (
        <>
            <PageTitle loading={loading} sx={{ mt: 4 }}>Select Facility</PageTitle>

            <FacilitySelectBase
                loading={loading}
                selectedFacility={selectedFacility}
                closestFacility={closestFacility}
                handleContinue={handleContinue}
                facilities={facilities}
            />
        </>
    );
}

// this could easily become generic since the facilities and setSelectedFacility are passed in...
export const FacilitySelect = ({ onContinue, facilities, setSelectedFacility, closestFacility }: {
    onContinue?: () => void,
    facilities: IFacility[],
    closestFacility?: IFacility | null,
    setSelectedFacility: (facility: IFacility) => void
}) => {
    const navigate = useStoreSelfNavigation();
    useAnalytics("FacilitySelect", true)

    const { pathname } = useLocation()
    const pathnameRef = useRef(pathname)

    useEffect(() => {
        if (pathname !== pathnameRef.current) {
            onContinue && onContinue() // TODO: we should require onContinue now...
        }
    }, [pathname])


    const handleContinue = (value: IFacility) => {
        if (value) {
            setSelectedFacility!(value)
        }
        if (onContinue) {
            onContinue()
        } else {
            navigate(-1)
        }
    }

    return (
        <>
            <PageTitle sx={pathname === "/" ? { mt: 4 } : {}}>Select Facility</PageTitle>

            <FacilitySelectBase
                closestFacility={closestFacility}
                selectedFacility={null}
                handleContinue={handleContinue}
                loading={false}
                facilities={facilities}
            />
        </>
    );
};

const FacilitySelectBase = ({
    handleContinue,
    loading,
    facilities,
    selectedFacility,
    closestFacility,
}: { handleContinue: any; loading?: boolean; facilities: IFacility[]; selectedFacility?: IFacility | null; closestFacility?: IFacility | null }) => {
    const [value, setValue] = useState(selectedFacility)

    return (
        <>
            <Section sx={{ mt: 2, mb: 12 }}>
                <Subtitle1 loading={loading} center>Start typing any part of the address<br />to find facility.</Subtitle1>
                <Autocomplete
                    loading={loading}
                    fullWidth
                    id="facility-select-input"
                    options={facilities}
                    autoHighlight
                    autoFocus
                    value={value}
                    getOptionLabel={(option: IFacility) => address(option)}
                    renderOption={(props, option: IFacility) => (
                        <Box key={address(option)} component="li" {...props} sx={{ lineHeight: 1.1 }}>
                            {addressEl(option)}
                        </Box>
                    )}
                    onChange={(_, value) => {
                        setValue(value)
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Search for facility"
                            inputProps={{
                                ...params.inputProps,
                                autoComplete: 'new-password',
                            }}
                            InputProps={{
                                ...params.InputProps
                            }}
                        />
                    )}
                />

                {
                    closestFacility &&
                    <Subtitle2
                        center
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                            setValue(closestFacility)
                        }}
                        bold
                        brandColor>Use Closest Facility</Subtitle2>
                }
            </Section >

            <StorSelfButton
                analyticsTag="Select Facility"
                pending={loading}
                onClick={() => handleContinue(value)}>Continue</StorSelfButton>
        </>
    );
}